import type { DictDocFile } from '../../types/workspaceState';
import { formatCount } from '../../utils/plural';

/**
 * A typed, human one-liner describing a doc's provenance by `doc_kind`
 * (ship-gate Step 5, control 6) — replaces the generic "N dependency links"
 * with a line that says what the doc actually IS:
 *   - model_derived       → "derived · model vN"
 *   - dashboard_html       → "results · run <id>"
 *   - dashboard_notebook   → "notebook · run <id>"
 *   - code                 → "code · N files"
 *   - narrative / default  → "narrative · cites X findings, Y regs"
 *                            (falls back to a generic link count when there are
 *                             no refs/kind, so no information is lost)
 *
 * Shared by GeneratedDocumentsView + DocumentationView so the two surfaces can't
 * drift.
 */
export function formatProvenance(doc: DictDocFile): string {
  const kind = doc.doc_kind || null;
  const findings = doc.finding_refs?.length ?? 0;
  const regs = doc.regulatory_refs?.length ?? 0;

  if (kind === 'model_derived') {
    return `derived · model v${doc.derived_from_model_version ?? '—'}`;
  }
  if (kind === 'dashboard_html') {
    return doc.derived_from_run_id ? `results · run ${doc.derived_from_run_id}` : 'results dashboard';
  }
  if (kind === 'dashboard_notebook') {
    return doc.derived_from_run_id ? `notebook · run ${doc.derived_from_run_id}` : 'dashboard notebook';
  }
  if (kind === 'code') {
    const files = doc.source_artifact_refs?.length ?? 0;
    return files > 0 ? `code · ${formatCount(files, { one: 'file', other: 'files' })}` : 'code';
  }

  // narrative (or unspecified): surface the regulatory grounding.
  const parts: string[] = [];
  if (findings > 0) parts.push(formatCount(findings, { one: 'finding', other: 'findings' }));
  if (regs > 0) parts.push(formatCount(regs, { one: 'reg', other: 'regs' }));
  if (parts.length > 0) return `narrative · cites ${parts.join(', ')}`;

  const links =
    findings +
    regs +
    (doc.policy_ids?.length ?? 0) +
    (doc.doc_refs?.length ?? 0) +
    (doc.model_refs?.length ?? 0);
  return links > 0 ? formatCount(links, { one: 'dependency link', other: 'dependency links' }) : '';
}
